import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { useSMSConfig } from '../hooks/useSMSConfig';
import { useSMS } from '../hooks/useSMS';
import CloseButton from './shared/CloseButton';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 0;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e0e0e0;
`;

const Title = styled.h2`
  margin: 0;
  color: #333;
  font-size: 20px;
  font-weight: 600;
`;

const CloseButtonWrapper = styled.div`
  position: relative;
`;

const Content = styled.div`
  padding: 24px;
`;

const Section = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
  margin: 0 0 12px 0;
  color: #555;
  font-size: 16px;
  font-weight: 600;
`;

const ToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
`;

const ToggleSwitch = styled.button<{ enabled: boolean }>`
  width: 50px;
  height: 26px;
  border-radius: 13px;
  border: none;
  background: ${props => props.enabled ? '#4CAF50' : '#ccc'};
  position: relative;
  cursor: pointer;
  transition: background-color 0.3s;
  
  &::after {
    content: '';
    position: absolute;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: white;
    top: 2px;
    left: ${props => props.enabled ? '26px' : '2px'};
    transition: left 0.3s;
  }
`;

const ToggleLabel = styled.span`
  margin-left: 12px;
  font-size: 16px;
  color: #333;
  font-weight: 500;
`;

const PhoneInputWrapper = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`;

const PhoneInput = styled.input`
  flex: 1;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #4CAF50;
  }
`;

const AddButton = styled.button`
  padding: 10px 16px;
  background: #4CAF50;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  
  &:hover {
    background: #45a049;
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const PhoneList = styled.div`
  margin-top: 16px;
`;

const PhoneItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
  
  &:last-child {
    border-bottom: none;
  }
`;

const PhoneNumber = styled.span`
  font-size: 14px;
  color: #333;
`;

const RemoveButton = styled.button`
  background: #ff4444;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 12px;
  
  &:hover {
    background: #cc0000;
  }
`;

const InfoText = styled.p`
  font-size: 14px;
  color: #666;
  line-height: 1.4;
  margin: 16px 0;
`;

const TestButton = styled.button`
  padding: 12px 16px;
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background 0.2s ease;

  &:hover {
    background: #1d4ed8;
  }

  &:disabled {
    background: #93c5fd;
    cursor: not-allowed;
  }
`;

const TestStatus = styled.div<{ success?: boolean }>`
  margin-top: 12px;
  font-size: 13px;
  line-height: 1.4;
  color: ${({ success }) => (success ? '#047857' : '#b91c1c')};
`;

const Footer = styled.div`
  padding: 16px 24px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: flex-end;
`;

const ActionButton = styled.button`
  background: #4CAF50;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 12px 24px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  
  &:hover {
    background: #45a049;
  }
`;

interface SMSSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SMSSettingsModal: React.FC<SMSSettingsModalProps> = ({ isOpen, onClose }) => {
  const { smsConfig, addPhoneNumber, removePhoneNumber, toggleEnabled } = useSMSConfig();
  const { sendTestMessage, isSending, lastResult } = useSMS();
  const [newPhoneNumber, setNewPhoneNumber] = useState('');

  const handleAddPhone = useCallback(() => {
    if (newPhoneNumber.trim()) {
      const added = addPhoneNumber(newPhoneNumber.trim());
      if (added) {
        setNewPhoneNumber('');
      }
    }
  }, [newPhoneNumber, addPhoneNumber]);

  const handleSendTest = useCallback(async () => {
    await sendTestMessage(smsConfig);
  }, [sendTestMessage, smsConfig]);

  const isTestDisabled =
    !smsConfig.enabled || smsConfig.phoneNumbers.length === 0 || isSending;

  const handlePhoneInputKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleAddPhone();
    }
  }, [handleAddPhone]);

  if (!isOpen) return null;

  return (
    <Overlay onClick={(e) => {
      e.stopPropagation();
      onClose();
    }}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>SMS Settings</Title>
          <CloseButtonWrapper>
            <CloseButton onClick={onClose} />
          </CloseButtonWrapper>
        </Header>
        
        <Content>
          <Section>
            <ToggleWrapper>
              <ToggleSwitch enabled={smsConfig.enabled} onClick={toggleEnabled} />
              <ToggleLabel>
                {smsConfig.enabled ? 'SMS Enabled' : 'SMS Disabled'}
              </ToggleLabel>
            </ToggleWrapper>
            <InfoText>
              When enabled, health scan results will be automatically sent via SMS to the configured phone numbers after each measurement.
            </InfoText>
          </Section>

          <Section>
            <SectionTitle>Phone Numbers</SectionTitle>
            <PhoneInputWrapper>
              <PhoneInput
                type="tel"
                placeholder="Enter phone number (e.g., +1234567890)"
                value={newPhoneNumber}
                onChange={(e) => setNewPhoneNumber(e.target.value)}
                onKeyPress={handlePhoneInputKeyPress}
                disabled={!smsConfig.enabled}
              />
              <AddButton 
                onClick={handleAddPhone}
                disabled={!smsConfig.enabled || !newPhoneNumber.trim()}
              >
                Add
              </AddButton>
            </PhoneInputWrapper>
            
            {smsConfig.phoneNumbers.length > 0 && (
              <PhoneList>
                {smsConfig.phoneNumbers.map((phone, index) => (
                  <PhoneItem key={index}>
                    <PhoneNumber>{phone}</PhoneNumber>
                    <RemoveButton 
                      onClick={() => removePhoneNumber(phone)}
                      disabled={!smsConfig.enabled}
                    >
                      Remove
                    </RemoveButton>
                  </PhoneItem>
                ))}
              </PhoneList>
            )}
            
            <InfoText>
              Add phone numbers in international format (e.g., +1234567890). US numbers without country code will automatically get +1 prefix.
            </InfoText>
          </Section>

          <Section>
            <SectionTitle>Connectivity Test</SectionTitle>
            <InfoText>
              Send a test message to confirm SMS delivery without starting a health scan. We will use the configured phone numbers above.
            </InfoText>
            <TestButton
              onClick={handleSendTest}
              disabled={isTestDisabled}
            >
              {isSending ? 'Sending Test SMS...' : 'Send Test SMS'}
            </TestButton>
            {lastResult && (
              <TestStatus success={lastResult.success}>
                {lastResult.message}
              </TestStatus>
            )}
          </Section>
        </Content>
        
        <Footer>
          <ActionButton onClick={onClose}>
            Done
          </ActionButton>
        </Footer>
      </ModalContent>
    </Overlay>
  );
};

export default SMSSettingsModal;
